import { ReactElement, useCallback, useMemo } from 'react';

import { VisitAndAppointmentView, VisitEncounterMode } from '@dreryk/egabinet-visit-frontend-api';

import { Translate, useBoolean } from '@dreryk/react-common';
import { Dropdown, useHistoryPushWithBackAction } from '@dreryk/react-components';

import { protectFeature } from '~libs.shared.system';
import { visitPaths } from '~libs.desktop.shared.paths';
import { VideoConsultationPatientContactUpdateShowModalEventInterceptor } from '~libs.desktop.video-consultation';

import { appointmentsTranslationKeys } from '../../../../translations';
import { VisitReopenModal } from '../../../../visit/reopen/VisitReopenModal';

type VisitReopenDropdownItemProps = {
   appointmentId?: number;
   patientId: number;
   visitId: number;
   ownedByCurrentUser: VisitAndAppointmentView['ownedByCurrentUser'];
   encounterMode: VisitEncounterMode;
};

type UseVisitReopenDropdownItemOutput = {
   VisitReopenDropdownItem: (props: VisitReopenDropdownItemProps) => ReactElement | null;
   visitReopenModal: ReactElement;
};

type UseVisitReopenDropdownItemOptions = {
   visitId: number;
};

export const useVisitReopenDropdownItem = (
   options: UseVisitReopenDropdownItemOptions
): UseVisitReopenDropdownItemOutput => {
   const creatingVisitsAvailable = protectFeature('creatingVisits');
   const [{ setTrue: openModal, setFalse: closeModal }, modalOpened] = useBoolean();
   const push = useHistoryPushWithBackAction(true);
   const goToVisit = useCallback(() => {
      push(
         visitPaths.getVisitInterviewPath({
            params: {
               visitId: options.visitId,
            },
         }),
         {}
      );
   }, [push, options.visitId]);

   return useMemo(
      () => ({
         VisitReopenDropdownItem: (props) => {
            const hide = Dropdown.useHide();
            const videoConsultationEnabled = props.encounterMode === 'VIDEO_CONSULTATION';

            return protectFeature(
               'searchingVisitOperations',
               <VideoConsultationPatientContactUpdateShowModalEventInterceptor
                  onClose={hide}
                  shouldShowModal={videoConsultationEnabled}
                  patientId={props.patientId}
               >
                  <Dropdown.Item
                     testId={props.appointmentId ? `visitReopen${props.appointmentId}` : 'visitReopen'}
                     disabled={!props.ownedByCurrentUser || !creatingVisitsAvailable}
                     onClick={(hide) => {
                        openModal();
                        hide();
                     }}
                  >
                     <Dropdown.Item.Tooltip
                        testId={'reopenVisit'}
                        content={appointmentsTranslationKeys.accountPartiallyBlocked}
                        disabled={creatingVisitsAvailable}
                     >
                        <Translate>{appointmentsTranslationKeys.reopenVisit}</Translate>
                     </Dropdown.Item.Tooltip>
                  </Dropdown.Item>
               </VideoConsultationPatientContactUpdateShowModalEventInterceptor>,
               null
            );
         },
         visitReopenModal: (
            <>
               {modalOpened ? (
                  <VisitReopenModal
                     onCancel={closeModal}
                     visitId={options.visitId}
                     onSuccess={() => {
                        goToVisit();
                     }}
                  />
               ) : null}
            </>
         ),
      }),
      [creatingVisitsAvailable, goToVisit, modalOpened, openModal, options.visitId, closeModal]
   );
};
